import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, Link } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import { GetSchedulSyncEvents } from '../../store/ScheduleSync/ScheduleSyncEventAction';
import * as Helper from '../../util/Helper';
import ActiveToggle from '../misc/ActiveToggle';
import debounce from 'lodash/debounce';
import Toggle from '../misc/Toggle';
import StatusCode from '../../util/StatusCode';
import TableHorizontal from '../misc/TableHorizontal';
import Search from '../misc/Search';
import FilterBottomComponent from '../misc/FilterBottomComponent';
import RequestParser from '../../util/RequestParser';
import { PageResult } from '../../models/CoreModels';
import { ScheduleSync_CoreEventListing, ScheduleSyncExportSystemOptions } from '../../models/CoreScheduleSyncModels';
import { CacheRequestState } from '../../models/RequestState';
import { useTranslation } from 'react-i18next';
import SelectInput from '../misc/SelectInput';
import { RootState } from '../../store/store';



const ScheduleSyncEvents = () => {
    const { t } = useTranslation(['common', 'scheduleSync']);

    const dispatch = useDispatch();

    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState('');

    const events = useSelector<RootState, CacheRequestState<PageResult<ScheduleSync_CoreEventListing>>>(state => state.schedulesync_events.events);
    var filter = RequestParser.parseScheduleSyncEventSearchPageRequest(searchParams);

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
        GetSchedulSyncEvents(filter)(dispatch);
    }, [dispatch]);

    useEffect(() => {
        GetSchedulSyncEvents(filter, true)(dispatch);
        window.scrollTo(0, 0);
    }, [searchParams]);

    const updateFilter = (name: string, value?: string | boolean | number) => {
        if (name !== 'page') {
            searchParams.set('page', '1');
        }

        if (value !== undefined && value !== '' && value !== null) {
            if (typeof (value) === 'string') {
                searchParams.set(name, value);
            } else {
                searchParams.set(name, value.toString());
            }
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }

    const debouncedSearch = useCallback(
        debounce(search => {
            updateFilter('search', search);
        }, 300), [searchParams]);


    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        let search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const renderFilter = () => (
        <Row>
            <Col sm="6">
                <Search
                    value={search}
                    onChange={onSearch}
                    placeholder={t('events.search', { ns: 'scheduleSync' })}
                />
            </Col>
            <Col sm="3">
                <SelectInput
                    items={ScheduleSyncExportSystemOptions.map(x => ({ value: x, text: `${t('filter.on', { ns: 'common' })} ${t(`events.targetSystem.options.${x}`, { ns: 'scheduleSync' })}` }))}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='targetSystem'
                    selected={filter.targetSystem}
                    defaultText={t('events.targetSystem.defaultSelected', { ns: 'scheduleSync' })}
                />
            </Col>
            <Col sm="3" className="flex dir-column-ml space-between">
                <Toggle
                    title={t('events.toggleUpcomingDates', { ns: 'scheduleSync' })}
                    disabled={events.code === StatusCode.PENDING}
                    value={!(filter.onlyUpcomingDates ?? true)}
                    update={(active) => updateFilter('onlyUpcomingDates', !active)}
                    id='onlyUpcomingDates'
                />
                <ActiveToggle
                    onlyActive={filter.onlyActive ?? true}
                    update={(active) => updateFilter('onlyActive', active)}
                    disabled={events.code === StatusCode.PENDING}
                />
            </Col>
        </Row>
    );

    const mapEvents = () => {
        if (events.code === StatusCode.COMPLETE || (events.code === StatusCode.PENDING && events.data !== undefined)) {
            if (events.data === undefined) {
                return [];
            }
            return events.data.values?.map(e => ({
                id: e.eventId,
                data: [
                    { value: <Link to={`/schedulesync/event/${e.eventId}`}>{e.title}</Link> },
                    { value: Helper.formatDate(e.startTime) },
                    { value: Helper.formatDate(e.endTime) },
                    { value: e.schoolName },
                    { value: e.location },
                    { value: e.numberOfOrganizers },
                    { value: e.numberOfParticipants },
                    { value: Helper.active(e.active) }
                ]
            }));
        } else {
            return [];
        }
    }

    const renderEvents = () => (
        <TableHorizontal
            header={[
                { value: t('commonTableHeaders.title', { ns: 'scheduleSync' }) },
                { value: t('commonTableHeaders.startTime', { ns: 'scheduleSync' }) },
                { value: t('commonTableHeaders.endTime', { ns: 'scheduleSync' }) },
                { value: t('genericTableHeaders.school', { ns: 'common' }) },
                { value: t('commonTableHeaders.location', { ns: 'scheduleSync' }) },
                { value: t('events.tableHeaders.countOrganizers', { ns: 'scheduleSync' }) },
                { value: t('events.tableHeaders.countParticipants', { ns: 'scheduleSync' }) },
                { value: t('genericTableHeaders.active', { ns: 'common' }) }]}
            info={mapEvents()}
            error={events.code === StatusCode.ERROR}
            searching={events.code === StatusCode.PENDING}
            displayName={t('object.scheduleSync.calendarEvent', { ns: 'common', count: 2 })}
            noResMsg={t('noResult.events', { ns: 'scheduleSync' })}
        />);

    return (
        <div className="container events_schedulesync">
            <div className="header--fixed">
                <h1>{t('object.scheduleSync.calendarEvent', { ns: 'common', count: 2 })}</h1>
                {renderFilter()}
            </div>
            <div className="body--scroll padding-40">
                {renderEvents()}
            </div>

            <FilterBottomComponent
                requestState={events}
                clearFilter={clearFilter}
                updateFilter={(value) => updateFilter('page', value)}
            />
        </div>
    )
}

export default ScheduleSyncEvents;
